import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import NumberFormat from 'react-number-format';
import { Formik, FieldArray } from 'formik';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Icon,
  MenuItem,
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  TextField
} from '@material-ui/core';
import { updateQuoteProListingApi } from 'api';
import clsx from 'clsx';
import useStyles from '../styles';
import { calculateDiscountValueField } from './helpers/quoteDiscounts';
import { addDiscountsValidationSchema } from './helpers/addDiscountsValidationSchema';


interface IDiscountOption {
  price?: number,
  price_type?: 'percent' | 'fixed',
  sort_order?: number
}

type DiscountOptionErrors = IDiscountOption | null;

const AddDiscounts = ({open, onClose}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    editableQuoteProduct
  } = useSelector((state: IReduxState) => state.quote);

  const existingQuoteproDiscounts = editableQuoteProduct?.custom_attributes?.find((attr) => attr.attribute_code === 'quotepro_discounts');
  const existingValues = existingQuoteproDiscounts?.value ? JSON.parse(existingQuoteproDiscounts.value) : [];

  const setSortOrder = (values) => {
    const sortOrders = values.map((value) => Number(value.sort_order));
    const maxOrder = sortOrders.length ? sortOrders.reduce((a, b) => Math.max(a, b)) : 0;
    return maxOrder + 1;
  };

  const addNewDiscountOption = (values) => {
    const sortOrder = setSortOrder(values);
    return {
      price: null,
      price_type: 'fixed',
      sort_order: sortOrder
    };
  };

  const handleSaveDiscounts = (optionData) => {
    const { custom_attributes } = editableQuoteProduct;

    const updatedDiscounts = optionData.values.map(({ price_type, sort_order, price }) => ({
      price_type,
      price,
      sort_order,
    }));

    const existingQuoteproDiscountsValue = existingQuoteproDiscounts
      ? JSON.parse(existingQuoteproDiscounts.value)
      : [];

    const allDiscounts = [...existingQuoteproDiscountsValue, ...updatedDiscounts];

    const editedProduct = {
      ...editableQuoteProduct,
      custom_attributes: custom_attributes.map((attr) =>
        attr.attribute_code !== 'quotepro_discounts'
          ? attr
          : { ...attr, value: JSON.stringify(allDiscounts) }
      ),
    };

    if (!editedProduct.custom_attributes.some((attr) => attr.attribute_code === 'quotepro_discounts')) {
      editedProduct.custom_attributes.push({
        attribute_code: 'quotepro_discounts',
        value: JSON.stringify(allDiscounts),
      });
    }

    updateQuoteProListingApi({ dispatch, product: editedProduct });
    onClose(false);
  };

  return (
    <Dialog
      fullWidth
      maxWidth={'sm'}
      open={open}
      onClose={() => onClose(false)}
      aria-labelledby="add-discount-options"
    >
      <DialogTitle className="flex justify-end p-1">
        <IconButton
          onClick={() => onClose(false)}
        >
          <Icon>close</Icon>
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Formik
          initialValues={{
            values: [{...{price: null, price_type: 'fixed'}, sort_order: setSortOrder(existingValues)}]
          }}
          enableReinitialize
          validationSchema={
            addDiscountsValidationSchema(
              editableQuoteProduct?.price,
              existingValues
            )
          }
          onSubmit={handleSaveDiscounts}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue
          }) => (
            <form onSubmit={handleSubmit}>
              <FieldArray name="values">
                {({remove, push}) => {
                  return (
                    <>
                      <TableContainer className={classes.tableContainer}>
                        <Typography className={classes.tableTitle}>
                          Discount
                        </Typography>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Type</TableCell>
                              <TableCell>Discount</TableCell>
                              <TableCell>Discount Value</TableCell>
                              <TableCell width="50px" />
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {values.values?.map((discountValue, index) => {
                              return (
                                <TableRow key={`discount-${index}`}>
                                  <TableCell className="align-top">
                                    <TextField
                                      select
                                      fullWidth
                                      variant="outlined"
                                      name={`values.[${index}].price_type`}
                                      SelectProps={{
                                        placeholder: 'Discount Type'
                                      }}
                                      value={discountValue.price_type}
                                      onChange={(event) => {
                                        const {value} = event.target;
                                        const title = value.replace('_', ' ');
                                        const priceType = value === 'percent'
                                          ? 'percent'
                                          : 'fixed';

                                        handleChange(event);
                                        setFieldValue(`values.[${index}].title`, title);
                                        setFieldValue(`values.[${index}].price_type`, priceType);
                                        setFieldValue(`values.[${index}].price`, discountValue.price ? discountValue.price : null);
                                      }}
                                      onBlur={handleBlur}
                                      error={touched?.values?.[index] &&
                                        errors.values?.[index] &&
                                        Boolean((errors.values[index] as DiscountOptionErrors)?.price_type)
                                      }
                                      helperText={touched?.values?.[index] &&
                                        errors.values?.[index] &&
                                        (errors.values?.[index] as DiscountOptionErrors)?.price_type
                                      }
                                    >
                                      <MenuItem value="percent">Percentage Off</MenuItem>
                                      <MenuItem value="fixed">Dollar Off</MenuItem>
                                    </TextField>
                                  </TableCell>
                                  <TableCell className="align-top">
                                    <NumberFormat
                                      thousandSeparator
                                      decimalScale={2}
                                      fullWidth
                                      suffix={discountValue.price_type === 'percent' ? '%' : ''}
                                      prefix={discountValue.price_type !== 'percent' ? '$' : ''}
                                      name={`values.[${index}].price`}
                                      value={discountValue.price ? Math.abs(discountValue.price) : null}
                                      variant="outlined"
                                      placeholder={`${discountValue.price_type === 'percent' ? '0%' : '$0.00'}`}
                                      onValueChange={(val) => setFieldValue(
                                        `values.[${index}].price`,
                                        val.floatValue ? val.floatValue : null
                                      )
                                      }
                                      onBlur={handleBlur}
                                      customInput={TextField}
                                      error={touched?.values?.[index] &&
                                        errors.values?.[index] &&
                                        Boolean((errors.values[index] as DiscountOptionErrors)?.price)
                                      }
                                      helperText={touched?.values?.[index] &&
                                        errors.values?.[index] &&
                                        (errors.values?.[index] as DiscountOptionErrors)?.price
                                      }
                                    />
                                  </TableCell>
                                  <TableCell className="align-top">
                                    <div className={clsx('flex items-center pl-3', classes.textField)}>
                                      {calculateDiscountValueField(discountValue.price_type, editableQuoteProduct.price, discountValue.price)}
                                    </div>
                                  </TableCell>
                                  <TableCell className="align-top">
                                    {values.values.length > 1 ?
                                      <IconButton
                                        size="small"
                                        onClick={() => remove(index)}
                                        className="mt-2"
                                      >
                                        <Icon fontSize="small">delete</Icon>
                                      </IconButton>
                                      : null
                                    }
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <div className="flex items-center justify-between w-full my-6">
                        <div>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => push(addNewDiscountOption(values.values))}
                          >
                            Add Discount
                          </Button>
                        </div>
                        <div>
                          <Button
                            variant="outlined"
                            onClick={() => onClose(false)}
                            className="mr-3"
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                          >
                            Create Discount
                          </Button>
                        </div>
                      </div>
                    </>
                  );
                }}
              </FieldArray>
            </form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default AddDiscounts;
